import { ReactNode } from 'react';

import {
  RiChat3Line,
  RiDeleteBinLine,
  RiExternalLinkLine,
  RiPencilLine,
} from '@remixicon/react';

import { DateUtils } from '@spotify/confidence-core-react';
import { AttachmentFragment } from '@spotify/confidence-graphql';
import {
  ArtifactIcon,
  ArtifactListItem,
  ArtifactMenu,
  isSafeUrl,
} from '@spotify/plugin-confidence-artifacts-react';

interface ExternalUrlArtifactListItemProps {
  artifact: AttachmentFragment;
  icon: ReactNode;
  subtitleLabel: string;
  openLabel: string;
  // Optional UI URL. Omit when the artifact has no navigable UI.
  href?: string;
  onOpen?: () => void;
  title?: string;
  createTimeFormat?: string;
  fallbackTitle?: (artifact: AttachmentFragment) => string;
  onDelete?: () => void;
  onRename?: () => void;
}

const defaultFallbackTitle = (artifact: AttachmentFragment) => artifact.uri;

export const ExternalUrlArtifactListItem = ({
  artifact,
  icon,
  subtitleLabel,
  openLabel,
  href,
  onOpen,
  title,
  createTimeFormat = DateUtils.MONTH_DAY_FORMAT,
  fallbackTitle = defaultFallbackTitle,
  onDelete,
  onRename,
}: ExternalUrlArtifactListItemProps) => {
  const dateLabel = DateUtils.toDateString(
    new Date(artifact.createTime),
    createTimeFormat,
  );

  const safeUrl = href && isSafeUrl(href) ? href : undefined;

  const open = () => {
    if (onOpen) {
      onOpen();
      return;
    }
    if (safeUrl) {
      window.open(safeUrl, '_blank', 'noopener,noreferrer');
    }
  };
  const canOpen = Boolean(onOpen) || Boolean(safeUrl);

  return (
    <ArtifactListItem.Root onClick={canOpen ? open : undefined}>
      <ArtifactListItem.Icon>
        <ArtifactIcon>{icon}</ArtifactIcon>
      </ArtifactListItem.Icon>
      <ArtifactListItem.Content
        primary={title || artifact.displayName || fallbackTitle(artifact)}
        secondary={
          <>
            {subtitleLabel} &middot; {dateLabel}
          </>
        }
      />
      {(canOpen || onRename || onDelete) && (
        <ArtifactListItem.Action>
          <ArtifactMenu.Root aria-label="Artifact actions">
            {canOpen && (
              <ArtifactMenu.Item
                textValue={openLabel}
                icon={
                  onOpen ? (
                    <RiChat3Line size={16} />
                  ) : (
                    <RiExternalLinkLine size={16} />
                  )
                }
                onClick={open}
              >
                {openLabel}
              </ArtifactMenu.Item>
            )}
            {onRename && (
              <ArtifactMenu.Item
                textValue="Rename"
                icon={<RiPencilLine size={16} />}
                onClick={onRename}
              >
                Rename
              </ArtifactMenu.Item>
            )}
            {onDelete && (
              <ArtifactMenu.Item
                textValue="Delete"
                icon={<RiDeleteBinLine size={16} />}
                onClick={onDelete}
                destructive
              >
                Delete
              </ArtifactMenu.Item>
            )}
          </ArtifactMenu.Root>
        </ArtifactListItem.Action>
      )}
    </ArtifactListItem.Root>
  );
};
